import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { checkPermission } from "../../app/utils/helpers";
import { config } from "../../app/utils/config";
import MobileDashboardKpis from "./mobile/MobileDashboardKpis";
import MobileDashboardLatestLeaves from "./mobile/MobileDashboardLatestLeaves";
import MobileDashboardTypeBreakdown from "./mobile/MobileDashboardTypeBreakdown";
import MobileDashboardLeaveTrend from "./mobile/MobileDashboardLeaveTrend";
import MobileDashboardLeaveStatusDonut from "./mobile/MobileDashboardLeaveStatusDonut";
import MobileDashboardVcomp from "./mobile/MobileDashboardVcomp";
import MobileDashboardSafeCards from "./mobile/MobileDashboardSafeCards";
import MobileDashboardNews from "./mobile/MobileDashboardNews";
import MobileDashboardSafeLeavesTrend from "./mobile/MobileDashboardSafeLeavesTrend";

export default function MobileDashboardView() {
    const leaves = useSelector((state) => state.leaves.list);
    const selectedTeamsIds = useSelector((state) => state.teams.selectedTeamsIds);

    const leaveStats = useMemo(() => {
        const base = { total: 0, pending: 0, published: 0, validated: 0, deleted: 0, deleteRequested: 0 };
        if (!leaves || !leaves.length) return base;
        return leaves.reduce((acc, leave) => {
            acc.total += 1;
            switch (leave.status) {
                case "PENDING": acc.pending += 1; break;
                case "PUBLISHED": acc.published += 1; break;
                case "VALIDATED": acc.validated += 1; break;
                case "DELETE_REQUESTED": acc.deleteRequested += 1; break;
                case "DELETED": acc.deleted += 1; break;
                default: break;
            }
            return acc;
        }, base);
    }, [leaves]);

    const typeBreakdown = useMemo(() => {
        if (!leaves || !leaves.length) return [];
        const counts = leaves.reduce((acc, leave) => {
            const type = leave.type || "Autre";
            acc[type] = (acc[type] || 0) + 1;
            return acc;
        }, {});
        return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 6);
    }, [leaves]);

    const latestLeaves = useMemo(() => {
        if (!leaves || !leaves.length) return [];
        return [...leaves].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 6);
    }, [leaves]);

    const canViewLeaveInsights = checkPermission(
        config.scopes.leaves,
        config.permissionTypes.view,
        selectedTeamsIds && selectedTeamsIds.length ? selectedTeamsIds : null
    );

    return (
        <Box
            sx={{
                px: 1.5,
                py: 1.5,
                animation: "mobileDashFadeIn 350ms cubic-bezier(0, 0, 0.2, 1) both",
                "@keyframes mobileDashFadeIn": {
                    from: { opacity: 0, transform: "translateY(8px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                },
            }}
        >
            <Stack spacing={2}>
                {canViewLeaveInsights ? (
                    <>
                        <MobileDashboardKpis pendingLeaves={leaveStats.pending} leaveStats={leaveStats} />
                        <MobileDashboardLeaveTrend leaves={leaves} />
                        <MobileDashboardLeaveStatusDonut leaves={leaves} />
                        <MobileDashboardLatestLeaves latestLeaves={latestLeaves} leaveStats={leaveStats} />
                        <MobileDashboardTypeBreakdown typeBreakdown={typeBreakdown} leaveStats={leaveStats} />
                    </>
                ) : (
                    <>
                        <MobileDashboardSafeCards />
                        <MobileDashboardSafeLeavesTrend />
                    </>
                )}
                <MobileDashboardVcomp />
            </Stack>
        </Box>
    );
}
